import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from "@mui/material";

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

function ComplaintTypeStep({
  complaintType,
  onChange,
  showValidation = false,
  t,
}) {
  const types = [
    {
      id: "product",
      title: t("Products"),
      description: t("ProductComplaintDescription"),
      icon: <Inventory2OutlinedIcon />,
    },
    {
      id: "transport",
      title: t("Transports"),
      description: t("TransportComplaintDescription"),
      icon: <LocalShippingOutlinedIcon />,
    },
  ];

  const isInvalid = showValidation && !complaintType;

  return (
    <Box>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        {t("ComplaintType")}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        {t("ComplaintTypeDescription")}
      </Typography>

      {/* =====================================================
          REQUIRED
      ===================================================== */}

      <Typography
        variant="body2"
        color={isInvalid ? "error.main" : "text.secondary"}
        sx={{
          mb: 3,
          fontWeight: isInvalid ? 600 : 400,
        }}
      >
        * {t("RequiredField")}
      </Typography>

      {/* =====================================================
          TYPES
      ===================================================== */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
          },
          gap: 3,
          maxWidth: 800,
          mx: "auto",
        }}
      >
        {types.map((type) => {
          const selected = complaintType === type.id;

          return (
            <Card
              key={type.id}
              variant="outlined"
              sx={{
                borderRadius: 3,

                borderWidth: selected || isInvalid ? 2 : 1,

                borderColor: selected
                  ? "primary.main"
                  : isInvalid
                    ? "error.main"
                    : "divider",

                backgroundColor: selected ? "rgba(25, 118, 210, 0.04)" : "#fff",

                transition: "all 0.2s ease",

                transform: selected ? "translateY(-2px)" : "none",

                boxShadow: selected
                  ? "0 8px 24px rgba(25,118,210,0.12)"
                  : "none",
              }}
            >
              <CardActionArea
                onClick={() => onChange(type.id)}
                sx={{
                  height: "100%",
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  {/* ICON */}

                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      mx: "auto",
                      mb: 2,
                      borderRadius: 2,

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",

                      backgroundColor: selected
                        ? "primary.main"
                        : "rgba(25, 118, 210, 0.08)",

                      color: selected ? "#fff" : "primary.main",
                    }}
                  >
                    {React.cloneElement(type.icon, {
                      sx: {
                        fontSize: 32,
                      },
                    })}
                  </Box>

                  {/* TITLE */}

                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {type.title}
                  </Typography>

                  {/* DESCRIPTION */}

                  <Typography variant="body2" color="text.secondary">
                    {type.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {/* =====================================================
          VALIDATION ERROR
      ===================================================== */}

      {isInvalid && (
        <Typography
          variant="body2"
          color="error.main"
          sx={{
            mt: 3,
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          {t("ComplaintTypeRequired")}
        </Typography>
      )}
    </Box>
  );
}

export default ComplaintTypeStep;
